import { Injectable } from '@angular/core';

const SPECIAL_ATTRIBUTES = ['id', 'label', 'non-editable', 'help-text'];
const RISE_COMPONENT_LABELS: { [key: string]: string } = {
  'rise-image': 'Image',
  'rise-data-financial': 'Financial',
  'rise-text': 'Text',
  'rise-icon': 'Icon',
  'rise-rich-text': 'Rich Text',
  'rise-slides': 'Google Slides',
  'rise-video': 'Video',
  'rise-data-rss': 'RSS',
  'rise-data-calendar': 'Calendar',
  'rise-data-counter-down': 'Count Down',
  'rise-data-counter-up': 'Count Up',
  'rise-data-table': 'Data Table',
  'rise-playlist': 'Embedded Template',
  'rise-html': 'HTML Embed',
  'rise-folder': 'Images & Videos',
  'rise-web-page': 'Web Page',
  'rise-youtube': 'YouTube',
  'rise-powerbi': 'Power BI',
  'rise-document': 'Document',
  'rise-social-media-posts': 'Social Media Posts',
  'rise-social-media-followers': 'Social Media Followers',
  'rise-video-streaming': 'Video Streaming',
  'rise-screen-sharing': 'Screen Share Code',
};

export interface PackageJson {
  width: number;
  height: number;
  playUntilDone?: boolean;
  branding?: boolean;
  [key: string]: any;
}

export interface BlueprintAttribute {
  label: string;
  value: any;
}

export interface ComponentPosition {
  top: string;
  left: string;
  width: string;
  height: string;
}

export interface BlueprintComponent {
  type: string;
  id: string;
  label: string;
  nonEditable: boolean;
  helpText?: string;
  attributes: {
    [key: string]: BlueprintAttribute | { label: string; value: any[] };
  };
  position?: ComponentPosition;
}

export interface Blueprint {
  riseInit: boolean;
  width: number;
  height: number;
  playUntilDone: boolean;
  branding: boolean;
  components: BlueprintComponent[];
}

@Injectable({
  providedIn: 'root',
})
export class BlueprintGeneratorService {
  private readonly nonEditableComponents = ['rise-play-until-done'];

  constructor() {}

  /**
   * Generates a blueprint from HTML template content and package.json data
   * @param htmlContent The HTML template content as a string
   * @param packageData The package.json data object
   * @returns The generated blueprint object
   */
  generateBlueprint(htmlContent: string, packageData: PackageJson): Blueprint {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const width = packageData.width;
    const height = packageData.height;
    const playUntilDone = packageData.playUntilDone || false;
    const branding = packageData.branding ?? false;

    const riseInit = doc.querySelector('script[src*="rise-init"]') !== null;

    const components = this.extractComponents(doc);

    return {
      riseInit,
      width,
      height,
      playUntilDone,
      branding,
      components,
    };
  }

  private getDefaultLabel(element: Element): string {
    if (element.tagName.toLowerCase() === 'rise-data-counter') {
      const type = element.getAttribute('type');
      if (type) {
        const key = `rise-data-counter-${type.toLowerCase()}`;
        return RISE_COMPONENT_LABELS[key] || 'Count [Up/Down]';
      }
      return 'Count [Up/Down]';
    }

    return RISE_COMPONENT_LABELS[element.tagName.toLowerCase()] || '';
  }

  private attributesFor(element: Element): {
    [key: string]: BlueprintAttribute;
  } {
    const attributes: { [key: string]: BlueprintAttribute } = {};

    Array.from(element.attributes).forEach((attr) => {
      const name = attr.name;
      if (!SPECIAL_ATTRIBUTES.includes(name) && !name.endsWith('-label')) {
        const labelAttr = element.getAttribute(`${name}-label`);
        attributes[name] = {
          label: labelAttr || `template.${name}`,
          value: attr.value,
        };
      }
    });

    return attributes;
  }

  private attributesObjectFor(element: Element): { [key: string]: any } {
    const attributes: { [key: string]: any } = {};

    Array.from(element.attributes).forEach((attr) => {
      const name = attr.name;
      if (!SPECIAL_ATTRIBUTES.includes(name) && !name.endsWith('-label')) {
        attributes[name] = attr.value;
      }
    });

    return attributes;
  }

  private positionFor(element: Element): ComponentPosition | undefined {
    const style = (element as HTMLElement).style;

    if (style.position !== 'absolute') {
      return undefined;
    }

    return {
      top: style.top,
      left: style.left,
      width: style.width,
      height: style.height,
    };
  }

  private getChildElement(element: Element): Element | null {
    const children = Array.from(element.children).filter((child) =>
      child.tagName.toLowerCase().startsWith('rise-'),
    );

    return children.length > 0 ? children[0] : null;
  }

  private getPlaylistItems(element: Element): any[] {
    return Array.from(element.children)
      .filter((child) => child.tagName.toLowerCase() === 'rise-playlist-item')
      .map((child) => {
        const mapped = this.attributesObjectFor(child);
        const childElement = this.getChildElement(child);

        if (childElement) {
          mapped['element'] = {
            tagName: childElement.tagName.toLowerCase(),
            label:
              childElement.getAttribute('label') ||
              this.getDefaultLabel(childElement),
            helpText: childElement.getAttribute('help-text') || undefined,
            attributes: this.attributesObjectFor(childElement),
          };
        }

        return mapped;
      });
  }

  private extractComponents(doc: Document): BlueprintComponent[] {
    const allElements = doc.querySelectorAll('*');
    const components: BlueprintComponent[] = [];

    allElements.forEach((element) => {
      const tagName = element.tagName.toLowerCase();

      if (
        tagName.startsWith('rise-') &&
        (!element.parentElement ||
          !/^rise-playlist/.test(element.parentElement.tagName.toLowerCase()))
      ) {
        const nonEditable =
          element.hasAttribute('non-editable') ||
          this.nonEditableComponents.includes(tagName);
        const attributes = this.attributesFor(element);

        if (tagName === 'rise-playlist') {
          attributes['items'] = {
            label: 'template.items',
            value: this.getPlaylistItems(element),
          };
        }

        components.push({
          type: tagName,
          id: element.getAttribute('id') || '',
          label: element.getAttribute('label') || this.getDefaultLabel(element),
          nonEditable,
          helpText: element.getAttribute('help-text') || undefined,
          attributes,
          position: this.positionFor(element),
        });
      }
    });

    return components;
  }
}
